import {
  PersistedFormGroup,
  PersistedFormArray,
  PersistedArray,
} from './persisted-form-group';
import type {
  PersistedFormGroupOptions,
  PersistedFormArrayOptions,
  PersistedArrayOptions,
} from './persisted-form-group';
import type { SchemaFormControl } from '@apexdesigner/schema-forms';
import { PackageVersion } from './package-version';
export { PackageVersion };
import { packageVersionSchema } from '@schemas/business-objects/package-version';
import { NpmPackageFormGroup } from './npm-package-form-group';
import { UserFormGroup } from './user-form-group';

export class PackageVersionFormGroup extends PersistedFormGroup {
  declare controls: {
    id: SchemaFormControl;
    version: SchemaFormControl;
    manifestJson: SchemaFormControl;
    readme: SchemaFormControl;
    shasum: SchemaFormControl;
    integrity: SchemaFormControl;
    publishedAt: SchemaFormControl;
    publishedFromRepository: SchemaFormControl;
    publishedFromWorkflow: SchemaFormControl;
    publishedFromRunId: SchemaFormControl;
    actor: SchemaFormControl;
    deprecatedMessage: SchemaFormControl;
    source: SchemaFormControl;
    fileIndexJson: SchemaFormControl;
    unpackedAt: SchemaFormControl;
    npmPackageId: SchemaFormControl;
    npmPackage: NpmPackageFormGroup;
    publishedById: SchemaFormControl;
    publishedBy: UserFormGroup;
  };

  declare value: Partial<PackageVersion>;

  constructor(
    data?: Record<string, any> | null,
    options?: PersistedFormGroupOptions,
  ) {
    super(packageVersionSchema, PackageVersion, data, options);
  }

  get object(): PackageVersion {
    return new PackageVersion(this.getRawValue());
  }

  protected override createControl(name: string) {
    switch (name) {
      case 'npmPackage':
        return new NpmPackageFormGroup();
      case 'publishedBy':
        return new UserFormGroup();
      default:
        return undefined;
    }
  }

  static async find(filter?: any): Promise<PackageVersion[]> {
    return PackageVersion.find(filter);
  }

  static async findOne(filter?: any): Promise<PackageVersion | null> {
    return PackageVersion.findOne(filter);
  }

  static async findById(id: number, filter?: any): Promise<PackageVersion> {
    return PackageVersion.findById(id, filter);
  }

  static async create(data: Partial<PackageVersion>): Promise<PackageVersion> {
    return PackageVersion.create(data as any);
  }

  static async updateById(
    id: number,
    updates: Partial<PackageVersion>,
  ): Promise<PackageVersion> {
    return PackageVersion.updateById(id, updates as any);
  }

  static async deleteById(id: number): Promise<boolean> {
    return PackageVersion.deleteById(id);
  }

  async fileContent(path: string): Promise<void> {
    const instance = new PackageVersion(this.getRawValue());
    return instance.fileContent(path);
  }

  async files(): Promise<{
    unpackedAt: Date;
    readme?: string;
    files: { path: string; size: number; text: boolean }[];
  }> {
    const instance = new PackageVersion(this.getRawValue());
    return instance.files();
  }

  async fileData(path: string): Promise<{
    path: string;
    contentType: string;
    text: boolean;
    base64Content: string;
  }> {
    const instance = new PackageVersion(this.getRawValue());
    return instance.fileData(path);
  }
}

export class PackageVersionFormArray extends PersistedFormArray {
  declare controls: PackageVersionFormGroup[];

  readonly entityName = 'PackageVersion' as const;

  constructor(options?: PersistedFormArrayOptions) {
    super(packageVersionSchema, PackageVersion, options);
  }

  protected override createItemGroup() {
    return new PackageVersionFormGroup();
  }

  get array(): PackageVersion[] {
    return this.controls.map((group: PackageVersionFormGroup) => group.object);
  }
}

export class PackageVersionPersistedArray extends PersistedArray<PackageVersion> {
  readonly entityName = 'PackageVersion' as const;

  constructor(options?: PersistedArrayOptions) {
    super(PackageVersion, options);
  }
}
